import { IFeatureToggle, IVehicle } from '@agenda-customer-ui/shared';
import { IUserProfileResponse } from '@core/interface';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NotificationService } from '../../../notification/services/notification.service';
import { FeatureToggleCodes, NewPushNotificationService, NewScheduleService, SessionService } from '@agenda-customer-ui/data-access';
import { Router } from '@angular/router';
import { COUNTRY_CODE, PaymentStatusEnum, RoutesPath, SchedulingModalityEnum, StorageKeys, TypeResourceMessage } from '@core/enums';
import { IOpenSchedule, IPayloadDriver } from '@core/interface';
import { CommonModule } from '@angular/common';

import { DialogComponent } from '@components/atoms';
import { ButtonLibComponent } from '@components/atoms';
import { createUrlOsbProjects } from '../../../../../../../../tools/utils/url-osb';
import { StatusPaymentComponent } from '../../../payments/status/status-payment.component';
import { AlertInformationComponent } from '../../../alert/alert-information.component';
import { DriverInformationComponent } from '../driver/driver-information.component';
import { environment } from '@core/envs';
import { scrollToTop } from '@core/utils';
import { SharedModuleLib } from '../../../../shared.module';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'app-open-schedule-card',
  templateUrl: './card-open-schedule.component.html',
  styleUrls: ['./card-open-schedule.component.scss'],
  standalone: true,
  imports: [CommonModule, TranslateModule, SharedModuleLib, DialogComponent, ButtonLibComponent, StatusPaymentComponent, AlertInformationComponent, DriverInformationComponent]
})
export class OpenScheduleCardComponent implements OnInit {
  @Input() schedule!: IOpenSchedule;
  @Input() isProjectStatus = false;
  @Output() removed = new EventEmitter<number>();
  @Output() detailsToogle = new EventEmitter<boolean>();

  cancelDialogChange = false;
  scheduleToCancel!: IOpenSchedule;
  loadingCancel = false;
  rescheduleDialogChange = false;
  rescheduleUnavailableDialogChange = false;

  // payments
  hasOnlinePayment = false;
  hasErrorPayment = false;
  onlinePaymentsFeatureEnabled = false;

  // modalidade do agendamento
  modalityEnum = SchedulingModalityEnum;

  // motorista associado ao agendamento
  payloadDriver!: IPayloadDriver;

  constructor(
    private translate: TranslateService,
    private scheduleService: NewScheduleService,
    private notificationService: NotificationService,
    private newPushNotificationService: NewPushNotificationService,
    private router: Router,
    private sessionService: SessionService
  ) { }

  ngOnInit() {
    this.payloadDriver = {
      advisor: this.schedule.advisorId,
      country: this.schedule.country,
      date: this.schedule.scheduleDate,
      dealer: this.schedule.dealerId,
      hour: this.schedule.scheduleHour,
      vin: this.schedule.vin,
    };

    this.initializePaymentState();
  }

  capitalizeFirstLetter(text: string) {
    const textFormatted = this.translate.instant(text).toLocaleLowerCase();
    return textFormatted.charAt(0).toUpperCase() + textFormatted.slice(1);
  }

  goToDetails() {
    this.sessionService.setItem(StorageKeys.SCHEDULE_ID, this.schedule.scheduleId.toString());
    this.sessionService.setObject(StorageKeys.SCHEDULE_DETAILS, this.schedule);
    if (!this.isProjectStatus) {
      this.router.navigate([RoutesPath.STATUS_DETAILS]);
      return;
    }
    this.detailsToogle.emit(true);
  }

  openCancelDialog(schedule: IOpenSchedule) {
    this.cancelDialogChange = true;
    this.scheduleToCancel = schedule;
  }

  closeCancelDialog() {
    this.cancelDialogChange = false;
  }

  closeRescheduleDialog() {
    this.rescheduleDialogChange = false;
  }

  closeRescheduleUnavailableDialog() {
    this.rescheduleUnavailableDialogChange = false;
  }

  openRescheduleDialog(schedule: IOpenSchedule) {
    this.scheduleToCancel = schedule;

    if (!this.canReschedule) {
      this.rescheduleUnavailableDialogChange = true;
      return;
    }

    this.rescheduleDialogChange = true;
  }

  async performCancelAndReschedule(schedule: IOpenSchedule, isCancel = false) {
    const { scheduleId, dealerId, advisorId, scheduleDate, scheduleHour, paymentMethod } = schedule;
    const hasPaymentMethod = paymentMethod?.[0]?.online ?? false;

    if (isCancel) {
      this.loadingCancel = true;
      const { userGuid } = this.sessionService.getObject<IUserProfileResponse>(StorageKeys.USER_PROFILE);
      await this.cancelSchedule(schedule, userGuid ?? '');
      return;
    }

    // TODO: Futuramente isso vai mudar quando fizer o tunning do OSB
    const params = `scheduleId=${scheduleId}&reschedule=true&dealerId=${dealerId}&advisorId=${advisorId}&scheduleDate=${scheduleDate}&scheduleHour=${scheduleHour}&hasPaymentMethod=${hasPaymentMethod}`;
    const urlDefault = createUrlOsbProjects(
      environment.project.schedule,
      params
    );

    window.location.assign(urlDefault);
  }

  cancelSchedule = async (schedule: IOpenSchedule, userGuid: string) => {
    await new Promise((resolve) => {
      this.scheduleService.delete(schedule, userGuid, false).subscribe({
        complete: async () => {
          this.notificationService.add({
            text: this.translate.instant('schedule.cancel-schedule-confirmation'),
            severity: 'SUCCESS',
            timeout: 3000,
          });
          scrollToTop();
          this.sendCancelMessage(schedule);
          resolve(true);
        },
        error: () => {
          this.notificationService.add({
            text: this.translate.instant('schedule.cancel-schedule-error'),
            severity: 'ERROR',
            timeout: 5000,
          });
          resolve(true);
        },
      });
    });
  };

  sendCancelMessage(schedule: IOpenSchedule) {
    this.newPushNotificationService.send(schedule, TypeResourceMessage.CANCEL_SCHEDULE_PUSH, false);
    this.removed.emit(schedule.scheduleId);
  }

  // TODO: Criar um service geral para retornar user e vehicles, incluir essa func
  #getVehicle(vin: string): IVehicle {
    const vehicles = JSON.parse(sessionStorage.getItem('userVehicles') as string) as IVehicle[];
    return vehicles?.filter((vehicle) => vehicle.vin === vin)[0];
  }

  // TODO: Criar um service geral para retornar user e vehicles, incluir essa func
  #getUser(): IUserProfileResponse {
    return JSON.parse(sessionStorage.getItem('userProfile') as string);
  }

  #getFullAddress(address: string, city: string, state: string, postalCode: string | undefined, country: string): string {
    const validArguments = [];
    if (address && address.trim() !== '') {
      validArguments.push(address.trim());
    }
    if (city && city.trim() !== '') {
      validArguments.push(city.trim());
    }
    if (state && state.trim() !== '') {
      validArguments.push(state.trim());
    }
    if (postalCode && postalCode.trim() !== '' && country === COUNTRY_CODE.BRAZIL) {
      validArguments.push(postalCode.trim());
    }
    return validArguments.join(', ');
  }

  getFeatureToggleFromSession(feature: FeatureToggleCodes): boolean {
    const featureToggles = this.sessionService.getObject<{ [key in FeatureToggleCodes]?: IFeatureToggle }>(StorageKeys.FEATURE_TOGGLE);
    return featureToggles?.[feature]?.active ?? false;
  }

  initializePaymentState() {
    const currentPayment = this.schedule.paymentMethod?.[0];
    this.hasOnlinePayment = currentPayment?.online ?? false;
    this.hasErrorPayment = currentPayment?.status === PaymentStatusEnum.ERROR;
    this.onlinePaymentsFeatureEnabled = this.getFeatureToggleFromSession(FeatureToggleCodes.OSR_ONLINE_PAYMENTS_SCALE_UP);
  }

  get canReschedule(): boolean {
    if (!this.hasOnlinePayment)  return this.schedule.rescheduleEnabled;
 
    if (this.isPaymentBlockedForReschedule()) return false;
 
    return this.schedule.rescheduleEnabled && this.onlinePaymentsFeatureEnabled;
  }
 
  isPaymentBlockedForReschedule(): boolean {
    const blockedStatuses = [PaymentStatusEnum.IN_PROCESS, PaymentStatusEnum.IN_MEDIATION];
    const currentStatus = this.schedule.paymentMethod?.[0]?.status?.toLowerCase() as PaymentStatusEnum;
    return blockedStatuses.includes(currentStatus);
  }
}

